import { editDistance } from '@streamlain/search-extract';

import type {
  CalendarColumnRole,
  CalendarSingleView,
} from '../../calendar-view-manager.js';
import { calendarRoleAcceptsPropertyType } from '../../calendar-view-manager.js';

export type CalendarSetupProperty = ReturnType<
  CalendarSingleView['getBoundedRawPropertiesWindow']
>['properties'][number];

export type CalendarDetectedMatches = Readonly<
  Record<CalendarColumnRole, CalendarSetupProperty | undefined>
>;

export type CalendarDetectedBuckets = Readonly<
  Record<CalendarColumnRole, readonly CalendarSetupProperty[]>
>;

export interface CalendarDetectedColumns {
  readonly byRole: CalendarDetectedBuckets;
  readonly matches: CalendarDetectedMatches;
}

const CALENDAR_ROLE_ORDER: readonly CalendarColumnRole[] = [
  'start',
  'end',
  'allDay',
  'timezone',
  'calendarId',
  'recurrence',
  'recurrenceExdate',
  'recurrenceId',
  'locationPhysical',
  'locationVirtual',
  'attendees',
  'reminder',
  'visibility',
  'status',
  'description',
];

const ROLE_ALIASES: Readonly<Record<CalendarColumnRole, readonly string[]>> = {
  allDay: ['All day', 'Ganztag', 'Ganztägig'],
  attendees: [
    'Attendees',
    'Guests',
    'Invitees',
    'Participants',
    'Teilnehmer',
    'Personen',
    'Ressourcen',
  ],
  calendarId: ['Calendar', 'Kalender', 'Resource', 'Ressource', 'Kategorie'],
  description: [
    'Description',
    'Notes',
    'Details',
    'Beschreibung',
    'Notizen',
    'Agenda',
  ],
  end: ['End', 'Ende', 'Finish', 'Due', 'Deadline', 'Bis', 'Ziel'],
  locationPhysical: ['Address', 'Ort', 'Location', 'Place', 'Raum', 'Adresse'],
  locationVirtual: [
    'Meeting link',
    'Meet',
    'Zoom',
    'Teams',
    'Link',
    'URL',
    'Video',
  ],
  recurrence: ['Recurrence', 'Repeat', 'RRULE', 'Wiederholung', 'Serie'],
  recurrenceExdate: [
    'Recurrence exceptions',
    'Exception',
    'Exceptions',
    'EXDATE',
    'Ausnahme',
    'Ausnahmen',
  ],
  recurrenceId: ['Recurrence ID', 'Series ID', 'Serien-ID', 'Serien ID'],
  reminder: ['Reminder', 'Alert', 'Erinnerung', 'Erinnern'],
  start: ['Start', 'Begin', 'Beginning', 'Beginn', 'Von', 'Anfang'],
  status: [
    'Status',
    'State',
    'Zustand',
    'Bestätigt',
    'Confirmed',
    'Tentative',
    'Abgesagt',
    'Cancelled',
  ],
  timezone: ['Timezone', 'Time zone', 'TZ', 'Zeitzone'],
  visibility: [
    'Visibility',
    'Sichtbarkeit',
    'Öffentlich',
    'Privat',
    'Public',
    'Private',
  ],
};

const AUTO_ASSIGN_MIN_SCORE = 0.4;

export function calendarSetupPropertyReadonly(
  property: CalendarSetupProperty
): boolean {
  return (
    (property as { readonly$?: { value?: boolean } }).readonly$?.value === true
  );
}

export class CalendarColumnDetectionService {
  static scan(view: CalendarSingleView): CalendarDetectedColumns {
    const byRole = emptyBuckets();
    const writableProperties = view
      .getBoundedRawPropertiesWindow()
      .properties.filter(property => !calendarSetupPropertyReadonly(property));

    for (const property of writableProperties) {
      for (const role of CALENDAR_ROLE_ORDER) {
        if (calendarRoleAcceptsPropertyType(role, property.type$.value)) {
          byRole[role].push(property);
        }
      }
    }

    const matches = emptyMatches();
    const claimedRoles = new Set<CalendarColumnRole>();
    const claimedProperties = new Set<string>();
    const pairs: Array<{
      property: CalendarSetupProperty;
      role: CalendarColumnRole;
      score: number;
    }> = [];

    for (const role of CALENDAR_ROLE_ORDER) {
      for (const property of byRole[role]) {
        const score = roleColumnScore(role, property.name$.value);
        if (score >= AUTO_ASSIGN_MIN_SCORE) {
          pairs.push({ property, role, score });
        }
      }
    }

    pairs.sort((a, b) => b.score - a.score);
    for (const pair of pairs) {
      if (
        claimedRoles.has(pair.role) ||
        claimedProperties.has(pair.property.id)
      ) {
        continue;
      }
      matches[pair.role] = pair.property;
      claimedRoles.add(pair.role);
      claimedProperties.add(pair.property.id);
    }

    applyDateFallbacks(byRole, matches, claimedProperties);
    applyUniqueTypeFallbacks(byRole, matches, claimedProperties);

    return { byRole, matches };
  }
}

function applyDateFallbacks(
  byRole: Record<CalendarColumnRole, CalendarSetupProperty[]>,
  matches: Record<CalendarColumnRole, CalendarSetupProperty | undefined>,
  claimedProperties: Set<string>
): void {
  const dates = byRole.start;
  if (!dates.length) return;

  if (!matches.start && !matches.end && dates.length === 1) {
    const only = dates[0];
    if (!only) return;
    const endScore = roleColumnScore('end', only.name$.value);
    matches[endScore >= AUTO_ASSIGN_MIN_SCORE ? 'end' : 'start'] = only;
    claimedProperties.add(only.id);
    return;
  }

  if (!matches.start) {
    const start = dates.find(property => !claimedProperties.has(property.id));
    if (start) {
      matches.start = start;
      claimedProperties.add(start.id);
    }
  }
  if (!matches.end) {
    const end = dates.find(property => !claimedProperties.has(property.id));
    if (end) {
      matches.end = end;
      claimedProperties.add(end.id);
    }
  }
}

function applyUniqueTypeFallbacks(
  byRole: Record<CalendarColumnRole, CalendarSetupProperty[]>,
  matches: Record<CalendarColumnRole, CalendarSetupProperty | undefined>,
  claimedProperties: Set<string>
): void {
  const fallbackRoles: readonly CalendarColumnRole[] = [
    'allDay',
    'locationVirtual',
  ];
  for (const role of fallbackRoles) {
    if (matches[role]) continue;
    const options = byRole[role].filter(
      property => !claimedProperties.has(property.id)
    );
    if (options.length !== 1) continue;
    const only = options[0];
    if (!only) continue;
    matches[role] = only;
    claimedProperties.add(only.id);
  }
}

function emptyBuckets(): Record<CalendarColumnRole, CalendarSetupProperty[]> {
  const buckets = {} as Record<CalendarColumnRole, CalendarSetupProperty[]>;
  for (const role of CALENDAR_ROLE_ORDER) {
    buckets[role] = [];
  }
  return buckets;
}

function emptyMatches(): Record<
  CalendarColumnRole,
  CalendarSetupProperty | undefined
> {
  const matches = {} as Record<
    CalendarColumnRole,
    CalendarSetupProperty | undefined
  >;
  for (const role of CALENDAR_ROLE_ORDER) {
    matches[role] = undefined;
  }
  return matches;
}

function roleColumnScore(role: CalendarColumnRole, columnName: string): number {
  return Math.max(
    ...ROLE_ALIASES[role].map(alias => nameScore(alias, columnName))
  );
}

function normalize(input: string): string {
  return input
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[._\-\s]/g, '');
}

function tokenize(input: string): string[] {
  return input
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .split(/[._\-\s]+/)
    .filter(Boolean);
}

function nameScore(roleAlias: string, columnName: string): number {
  const alias = normalize(roleAlias);
  const column = normalize(columnName);
  if (!alias || !column) return 0;
  if (alias === column)
    return roleAlias.trim() === columnName.trim() ? 1 : 0.95;

  const dist = editDistance(alias, column);
  const maxLen = Math.max(alias.length, column.length);
  const similarity = 1 - dist / maxLen;
  if (similarity >= 0.7) return similarity;

  if (alias.length >= 3 && (alias.includes(column) || column.includes(alias))) {
    const ratio = Math.min(alias.length, column.length) / maxLen;
    return 0.5 + ratio * 0.3;
  }

  const aliasTokens = tokenize(roleAlias);
  const columnTokens = tokenize(columnName);
  if (!aliasTokens.length || !columnTokens.length) return 0;
  let matchedTokens = 0;
  for (const token of aliasTokens) {
    if (columnTokens.some(columnToken => columnToken === token)) {
      matchedTokens++;
    }
  }
  if (matchedTokens === 0) return 0;
  return (matchedTokens / aliasTokens.length) * 0.5;
}
